import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, CheckCircle2, Users, Zap, Shield, Bot } from 'lucide-react';

const Landing = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Redirect if already logged in
    React.useEffect(() => {
        if (user) {
            if (user.is_admin) {
                navigate('/admin');
            } else if (user.lifecycle_stage === 'preboarding' && user.preboarding_token) {
                // Redirect to preboarding dashboard
                navigate(`/preboard/${user.preboarding_token}/dashboard`);
            } else {
                navigate('/employee');
            }
        }
    }, [user, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
            {/* Navbar */}
            <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary-hover rounded-lg flex items-center justify-center text-white font-bold shadow-lg">
                                e
                            </div>
                            <span className="text-xl font-bold text-gray-800">
                                envion
                            </span>
                        </div>

                        {/* Nav Links */}
                        <div className="hidden md:flex items-center gap-8">
                            <a href="#features" className="text-gray-600 hover:text-primary font-medium transition-colors">
                                Features
                            </a>
                            <a href="#how-it-works" className="text-gray-600 hover:text-primary font-medium transition-colors">
                                How it Works
                            </a>
                            <a href="#pricing" className="text-gray-600 hover:text-primary font-medium transition-colors">
                                Pricing
                            </a>
                        </div>

                        {/* Auth Buttons */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/login')}
                                className="px-4 py-2 text-gray-700 hover:text-primary font-medium transition-colors"
                            >
                                Login
                            </button>
                            <button
                                onClick={() => navigate('/register')}
                                className="px-5 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                            >
                                Get Started
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left Content */}
                    <div>
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Zap size={16} />
                            AI-Powered Onboarding
                        </div>

                        <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                            Make Employee Onboarding
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary-hover"> Effortless</span>
                        </h1>

                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            envion is an AI-powered platform that makes onboarding simpler, faster, and less stressful for both companies and new hires.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-12">
                            <button
                                onClick={() => navigate('/register')}
                                className="px-8 py-4 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all flex items-center justify-center gap-2 group"
                            >
                                Start Free Trial
                                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button
                                onClick={() => navigate('/login')}
                                className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold border-2 border-gray-200 hover:border-primary hover:text-primary transition-all"
                            >
                                Watch Demo
                            </button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-6">
                            <div>
                                <p className="text-3xl font-bold text-gray-900">98%</p>
                                <p className="text-sm text-gray-600">Success Rate</p>
                            </div>
                            <div>
                                <p className="text-3xl font-bold text-gray-900">50%</p>
                                <p className="text-sm text-gray-600">Time Saved</p>
                            </div>
                            <div>
                                <p className="text-3xl font-bold text-gray-900">24/7</p>
                                <p className="text-sm text-gray-600">AI Support</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Feature Cards */}
                    <div className="relative">
                        <div className="grid gap-4">
                            {/* Card 1 */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <Bot size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 mb-2">AI-Powered Assistant</h3>
                                        <p className="text-gray-600 text-sm">Get instant answers to HR questions 24/7 with our intelligent chatbot.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2 */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 size={24} className="text-emerald-500" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 mb-2">Automated Workflows</h3>
                                        <p className="text-gray-600 text-sm">Streamline onboarding with automated task assignments and tracking.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Card 3 */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <Shield size={24} className="text-purple-500" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 mb-2">Compliance Tracking</h3>
                                        <p className="text-gray-600 text-sm">Stay compliant with automated tracking and reporting features.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Decorative Elements */}
                        <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl"></div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="bg-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
                        <p className="text-xl text-gray-600">Powerful features to streamline your onboarding process</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Users,
                                title: 'Employee Management',
                                description: 'Manage all your employees and their onboarding progress in one place.',
                                color: 'orange',
                            },
                            {
                                icon: CheckCircle2,
                                title: 'Task Automation',
                                description: 'Automatically assign and track onboarding tasks for new hires.',
                                color: 'emerald',
                            },
                            {
                                icon: Bot,
                                title: 'AI Assistant',
                                description: 'Get instant HR support with our intelligent AI chatbot.',
                                color: 'purple',
                            },
                        ].map((feature, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100"
                            >
                                <div className={`w-14 h-14 bg-${feature.color}-100 rounded-xl flex items-center justify-center mb-4`}>
                                    <feature.icon size={28} className={`text-${feature.color}-500`} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-primary to-primary-hover py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Onboarding?</h2>
                    <p className="text-xl text-white/90 mb-8">Join hundreds of companies using envion to streamline their HR operations.</p>
                    <button
                        onClick={() => navigate('/register')}
                        className="px-8 py-4 bg-white text-primary rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all inline-flex items-center gap-2 group"
                    >
                        Get Started for Free
                        <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-hover rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            e
                        </div>
                        <span className="text-lg font-bold text-white">
                            envion
                        </span>
                    </div>
                    <p className="text-sm">© 2025 envion. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;

